import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './Homework3Question.css';
import './Homework3Question3.css';

const Homework3Question3: React.FC = () => {
  const [originalImage, setOriginalImage] = useState<string | null>(null);
  const [processedImage, setProcessedImage] = useState<string | null>(null);
  const [whiteLevel, setWhiteLevel] = useState(184);
  const [blackLevel, setBlackLevel] = useState(51);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSampleModal, setShowSampleModal] = useState(false);

  // Sample images
  const sampleImages = [
    { url: 'https://upload.wikimedia.org/wikipedia/en/7/7d/Lenna_%28test_image%29.png', name: 'Lenna' },
    { url: '/sample-images/VK1.jpg', name: 'VK1' },
    { url: '/sample-images/VK2.jpg', name: 'VK2' },
    { url: '/sample-images/diyetika.png', name: 'Diyetika' },
    { url: '/sample-images/ailab_logo.png', name: 'AI Lab' },
    { url: '/sample-images/ikc.png', name: 'IKC' },
  ];

  // Auto-load Lenna as default sample image on component mount
  useEffect(() => {
    handleSampleImageSelect(sampleImages[0].url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSampleImageSelect = async (url: string) => {
    try {
      setShowSampleModal(false);
      setError(null);

      const response = await fetch(url);
      const blob = await response.blob();
      const reader = new FileReader();
      reader.onload = (e) => {
        setOriginalImage(e.target?.result as string);
        setProcessedImage(null);
      };
      reader.readAsDataURL(blob);
    } catch (err) {
      console.error('Failed to load sample image:', err);
      setError('Failed to load sample image');
    }
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setOriginalImage(e.target?.result as string);
        setProcessedImage(null);
        setError(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleWhiteLevelChange = (value: number) => {
    const newValue = Math.max(blackLevel + 3, Math.min(255, value));
    setWhiteLevel(newValue);
  };

  const handleBlackLevelChange = (value: number) => {
    const newValue = Math.max(0, Math.min(whiteLevel - 3, value));
    setBlackLevel(newValue);
  };

  const applyTransferFunction = async () => {
    if (!originalImage) {
      setError('Please upload an image first');
      return;
    }

    if (whiteLevel - blackLevel < 3) {
      setError('White and black levels must be at least 3 pixels apart');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const base64Image = originalImage.split(',')[1];

      const response = await axios.post(
        'https://us-central1-image-processing-vk.cloudfunctions.net/process_homework3_q3',
        {
          image: base64Image,
          operation: 'custom_transfer',
          white_level: whiteLevel,
          black_level: blackLevel
        },
        {
          headers: { 'Content-Type': 'application/json' },
          timeout: 30000
        }
      );

      if (response.data.success) {
        setProcessedImage(`data:image/png;base64,${response.data.processed_image}`);
      } else {
        setError(response.data.error || 'Processing failed');
      }
    } catch (err: any) {
      console.error('Error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to process image');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="hw3-question-container hw3-q3" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
      <div className="floating-shapes">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
      </div>

      <div className="content-wrapper">
        <Link to="/homework/3" className="back-button">
          ← Back to Homework 3
        </Link>

        <header className="question-header">
          <div className="badge">Question 3</div>
          <h1>Custom Transfer Function</h1>
          <p className="subtitle">Manual contrast control with adjustable levels</p>
        </header>

        <div className="algorithm-info">
          <h3>Algorithm</h3>
          <p>
            A piecewise linear transformation that maps pixels below the black level to 0, pixels above the white level to 255,
            and linearly interpolates pixels in between. This provides fine-grained control over contrast enhancement.
          </p>
          <div className="formula-block">
            <div>I<sub>out</sub> = 0, if I<sub>in</sub> ≤ L<sub>black</sub></div>
            <div>I<sub>out</sub> = 255 / (L<sub>white</sub> - L<sub>black</sub>) × (I<sub>in</sub> - L<sub>black</sub>), if L<sub>black</sub> &lt; I<sub>in</sub> &lt; L<sub>white</sub></div>
            <div>I<sub>out</sub> = 255, if I<sub>in</sub> ≥ L<sub>white</sub></div>
          </div>
        </div>

        <div className="controls-section">
          <div className="sliders-container">
            <div className="slider-group">
              <label>
                <span className="slider-label">White Level</span>
                <span className="slider-value">{whiteLevel}</span>
              </label>
              <input
                type="range"
                min="0"
                max="255"
                value={whiteLevel}
                onChange={(e) => handleWhiteLevelChange(parseInt(e.target.value))}
                className="level-slider white-slider"
              />
            </div>

            <div className="slider-group">
              <label>
                <span className="slider-label">Black Level</span>
                <span className="slider-value">{blackLevel}</span>
              </label>
              <input
                type="range"
                min="0"
                max="255"
                value={blackLevel}
                onChange={(e) => handleBlackLevelChange(parseInt(e.target.value))}
                className="level-slider black-slider"
              />
            </div>

            <div className="constraint-info">
              Minimum difference: 3 pixels (Current: {whiteLevel - blackLevel})
            </div>
          </div>

          <div className="upload-area">
            <button
              onClick={() => setShowSampleModal(true)}
              className="upload-label"
              style={{ cursor: 'pointer' }}
            >
              <span className="upload-icon">🖼️</span>
              <span>Select Sample</span>
            </button>
            <label htmlFor="image-upload" className="upload-label">
              <span className="upload-icon">📁</span>
              <span>Upload Image</span>
              <input
                id="image-upload"
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
              />
            </label>
            <button
              onClick={applyTransferFunction}
              disabled={!originalImage || loading}
              className="process-button"
            >
              {loading ? 'Processing...' : 'Apply Transfer Function'}
            </button>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
        </div>

        <div className="images-grid">
          <div className="image-panel">
            <h3>Original Image</h3>
            <div className="image-container">
              {originalImage ? (
                <img src={originalImage} alt="Original" />
              ) : (
                <div className="placeholder">Upload an image to start</div>
              )}
            </div>
          </div>

          <div className="image-panel">
            <h3>Processed Image</h3>
            <div className="image-container">
              {processedImage ? (
                <img src={processedImage} alt="Processed" />
              ) : (
                <div className="placeholder">
                  {loading ? 'Processing...' : 'Adjust levels and apply'}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sample Image Modal */}
      {showSampleModal && (
        <div className="modal-overlay" onClick={() => setShowSampleModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Select Sample Image</h3>
              <button className="modal-close" onClick={() => setShowSampleModal(false)}>×</button>
            </div>
            <div className="sample-grid">
              {sampleImages.map((sample, index) => (
                <div
                  key={index}
                  className="sample-item"
                  onClick={() => handleSampleImageSelect(sample.url)}
                >
                  <img src={sample.url} alt={sample.name} />
                  <span className="sample-name">{sample.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Homework3Question3;
